import { Box, Button, FormControl, FormControlLabel, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography, Dialog, DialogContent, DialogActions } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HelpIcon from '@mui/icons-material/Help';
import React, { useState } from 'react';
import { ContactService } from '@df/api';

const Contact = () => {
  const [userType, setUserType] = useState('individual');
  const [inquiryType, setInquiryType] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [role, setRole] = useState('');
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStatus, setModalStatus] = useState(null); // 'success' | 'error'
  const [modalMessage, setModalMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const isTeam = userType === 'company';

  const validate = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !message.trim()) return false;
    if (isTeam && (!companyName.trim() || !role.trim())) return false;
    // Basic email validation
    if (!/\S+@\S+\.\S+/.test(email)) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setModalStatus('error');
      setModalMessage('Please fill in all required fields with valid information.');
      setModalOpen(true);
      return;
    }
    setLoading(true);
    const payload = isTeam
      ? {
          type: 'Team',
          first_name: firstName,
          last_name: lastName,
          email,
          phone,
          company_name: companyName,
          role,
          how_can_datafre8_help: message,
        }
      : {
          type: 'Individual',
          first_name: firstName,
          last_name: lastName,
          email,
          how_can_datafre8_help: message,
        };
    try {
      const data = await ContactService.submitContactForm(payload);
      setModalStatus('success');
      setModalMessage(data.message || 'Contact form submitted!');
      setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setCompanyName(''); setRole(''); setMessage('');
    } catch (err) {
      setModalStatus('error');
      setModalMessage('Network error. Please try again.');
    } finally {
      setLoading(false);
      setModalOpen(true);
    }
  };

  return (
    <Box
      sx={{
        padding: '4rem 2rem',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'flex-start',
        minHeight: '80vh',
        width: '100%',
        maxWidth: '1400px',
        margin: '0 auto',
        gap: '3rem',
      }}
    >
      {/* Left Side: Pricing Info */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', background: '#f7faff', borderRadius: '20px', p: '3rem 2.5rem', boxShadow: '0 4px 24px rgba(44,90,160,0.07)', border: '1px solid #e3f2fd', minWidth: 0 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color: '#2c5aa0', mb: '1.5rem', fontSize: '2.5rem', letterSpacing: '-0.02em' }}>
          GET PRICING
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 500, color: '#1976d2', mb: '1.5rem', fontSize: '1.3rem' }}>
          Get custom pricing for datafre8
        </Typography>
        <Typography variant="body1" sx={{ color: '#333', fontSize: '1.1rem', mb: '1.5rem', lineHeight: 1.7 }}>
          Discover how forward-looking teams are unlocking the power of 3D and 4D machine learning with datafre8's unified, low-code platform. Talk to our team about use cases delivering measurable business outcomes, such as:
        </Typography>
        <ul style={{ color: '#444', fontSize: '1.08rem', marginBottom: '1.5rem', paddingLeft: '1.2rem', lineHeight: 1.7 }}>
          <li>A robotics company training autonomous navigation models faster and more reliably using advanced point cloud pipelines</li>
          <li>A drone mapping provider streamlining large-scale LiDAR data analysis with automated 3D machine learning workflows</li>
          <li>An industrial automation firm improving defect detection and reducing downtime with real-time 3D inspection and predictive maintenance</li>
        </ul>
        <Typography variant="body1" sx={{ color: '#333', fontSize: '1.1rem', mt: '0.5rem', lineHeight: 1.7 }}>
          Get a custom quote from a datafre8 expert. Learn how your team can build secure, scalable, and intuitive 3D/4D data pipelines with datafre8.
        </Typography>
      </Box>

      {/* Right Side: Contact Form */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', minWidth: 0, pr: '2.5rem', mr: '5rem' }}>
        {/* Contact Form */}
        <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: "flex",
        flexDirection: "column",
            gap: 2.5,
            width: '100%',
            maxWidth: '600px',
            p: 4,
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
            border: '1px solid #e8eaf6',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #2c5aa0, #1e3a5f)',
              borderRadius: '20px 20px 0 0'
            }
          }}
        >
          {/* User Type Selection */}
          <FormControl component="fieldset" sx={{ mb: 1 }}>
            <FormLabel component="legend" sx={{ fontWeight: "600", color: '#333', mb: 1.5, fontSize: '1rem' }}>
              I am contacting you as a: <span style={{ color: "red" }}>*</span>
            </FormLabel>
            <RadioGroup
              row
              value={userType}
              onChange={(e) => setUserType(e.target.value)}
              sx={{ gap: 3 }}
            >
              <FormControlLabel 
                value="individual" 
                control={<Radio sx={{ color: '#2c5aa0', '&.Mui-checked': { color: '#2c5aa0' } }} />} 
                label="Individual" 
                sx={{ 
                  '& .MuiFormControlLabel-label': { 
                    fontWeight: '500',
                    color: '#555',
                    fontSize: '1rem'
                  } 
                }}
              />
              <FormControlLabel 
                value="company" 
                control={<Radio sx={{ color: '#2c5aa0', '&.Mui-checked': { color: '#2c5aa0' } }} />} 
                label="Company" 
          sx={{
                  '& .MuiFormControlLabel-label': { 
                    fontWeight: '500',
                    color: '#555',
                    fontSize: '1rem'
                  } 
                }}
              />
            </RadioGroup>
          </FormControl>

          {/* First Name */}
          <Box>
            <InputLabel htmlFor="first-name" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              First Name <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <TextField
              id="first-name"
              variant="outlined"
              fullWidth
              required
              placeholder="Enter your first name"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 1 }}>
                    <PersonIcon sx={{ color: '#666', fontSize: '1.4rem' }} />
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: "#fafbfc",
                borderRadius: '12px',
                "& .MuiOutlinedInput-root": {
                  borderRadius: '12px',
                  padding: '4px 0',
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover fieldset": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                },
              }}
            />
          </Box>

          {/* Last Name */}
          <Box>
            <InputLabel htmlFor="last-name" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              Last Name <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <TextField
              id="last-name"
              variant="outlined"
              fullWidth
              required
              placeholder="Enter your last name"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 1 }}>
                    <PersonIcon sx={{ color: '#666', fontSize: '1.4rem' }} />
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: "#fafbfc",
                borderRadius: '12px',
                "& .MuiOutlinedInput-root": {
                  borderRadius: '12px',
                  padding: '4px 0',
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover fieldset": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                },
              }}
            />
          </Box>

          {/* Email Address */}
          <Box>
            <InputLabel htmlFor="email" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              Email Address <span style={{ color: "red" }}>*</span>
            </InputLabel>
            <TextField
              id="email"
              variant="outlined"
              fullWidth
              type="email"
              required
              placeholder="Enter your email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 1 }}>
                    <EmailIcon sx={{ color: '#666', fontSize: '1.4rem' }} />
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: "#fafbfc",
                borderRadius: '12px',
                "& .MuiOutlinedInput-root": {
                  borderRadius: '12px',
                  padding: '4px 0',
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover fieldset": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                },
              }}
            />
          </Box>

          {/* Phone Number (optional) */}
          <Box>
            <InputLabel htmlFor="phone" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              Phone Number <span style={{ color: "#666", fontSize: '0.9rem', fontWeight: '400' }}>(Optional)</span>
            </InputLabel>
            <TextField
              id="phone"
              variant="outlined"
              fullWidth
              placeholder="Enter your phone number"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', pl: 2, pr: 1 }}>
                    <PhoneIcon sx={{ color: '#666', fontSize: '1.4rem' }} />
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: "#fafbfc",
                borderRadius: '12px',
                "& .MuiOutlinedInput-root": {
                  borderRadius: '12px',
                  padding: '4px 0',
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover fieldset": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                },
              }}
            />
          </Box>

          {/* Company Name (only for Team/Company) */}
          {isTeam && (
            <Box>
              <InputLabel htmlFor="company-name" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
                Company Name <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <TextField
                id="company-name"
                variant="outlined"
                fullWidth
                required
                placeholder="Enter your company name"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                sx={{
                  backgroundColor: "#fafbfc",
                  borderRadius: '12px',
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '12px',
                    padding: '4px 0',
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderRadius: '12px',
                    },
                    "&:hover fieldset": {
                      borderColor: "#2c5aa0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2c5aa0",
                      borderWidth: "2px",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f5f7fa",
                  },
                }}
              />
            </Box>
          )}

          {/* Role (only for Team/Company) */}
          {isTeam && (
            <Box>
              <InputLabel htmlFor="role" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
                Role <span style={{ color: "red" }}>*</span>
              </InputLabel>
              <TextField
                id="role"
                variant="outlined"
                fullWidth
                required
                placeholder="Enter your role"
                value={role}
                onChange={e => setRole(e.target.value)}
                sx={{
                  backgroundColor: "#fafbfc",
                  borderRadius: '12px',
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '12px',
                    padding: '4px 0',
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                      borderRadius: '12px',
                    },
                    "&:hover fieldset": {
                      borderColor: "#2c5aa0",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#2c5aa0",
                      borderWidth: "2px",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "#f5f7fa",
                  },
                }}
              />
            </Box>
          )}

          {/* Inquiry Type Dropdown (optional, not sent to API) */}
          <Box>
            <InputLabel htmlFor="inquiry-type" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              Area of Interest <span style={{ color: "#666", fontSize: '0.9rem', fontWeight: '400' }}>(Optional)</span>
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="inquiry-type"
                value={inquiryType}
                onChange={(e) => setInquiryType(e.target.value)}
                displayEmpty
                sx={{
                  backgroundColor: "#fafbfc",
                  borderRadius: '12px',
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                  "&:hover": {
                    backgroundColor: "#f5f7fa",
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <span style={{ color: '#999' }}>Select an area of interest</span>
                </MenuItem>
                <MenuItem value="product-demo">Product Demo</MenuItem>
                <MenuItem value="support">Support</MenuItem>
                <MenuItem value="collaboration">Collaboration</MenuItem>
                <MenuItem value="general-interest">General Interest</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Message */}
          <Box>
            <InputLabel htmlFor="message" sx={{ fontWeight: "600", my: "0.5rem", color: '#333', fontSize: '1rem' }}>
              How can we assist you?
            </InputLabel>
            <TextField
              id="message"
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              required
              placeholder="Please describe your inquiry or the assistance you need."
              value={message}
              onChange={e => setMessage(e.target.value)}
              InputProps={{
                startAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', pl: 2, pr: 1, pt: 2 }}>
                    <HelpIcon sx={{ color: '#666', fontSize: '1.4rem' }} />
                  </Box>
                ),
              }}
              sx={{
                backgroundColor: "#fafbfc",
                borderRadius: '12px',
                "& .MuiOutlinedInput-root": {
                  borderRadius: '12px',
                  padding: '8px 0',
                  "& fieldset": {
                    borderColor: "#e0e0e0",
                    borderRadius: '12px',
                  },
                  "&:hover fieldset": {
                    borderColor: "#2c5aa0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#2c5aa0",
                    borderWidth: "2px",
                  },
                },
                "&:hover": {
                  backgroundColor: "#f5f7fa",
                },
              }}
            />
          </Box>

          {/* Privacy Notice */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.9rem',
              color: '#666',
              fontStyle: 'italic',
              textAlign: 'center',
              mt: 1,
              mb: 2
            }}
          >
            We respect your privacy. Your info is never shared.
          </Typography>

          {/* Send Message Button */}
          <Button 
            variant="contained" 
            type="submit"
            disabled={loading}
            sx={{
              my: '1rem',
              py: 2,
              px: 4,
              backgroundColor: '#2c5aa0',
              '&:hover': {
                backgroundColor: '#1e3a5f',
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(44, 90, 160, 0.3)',
              },
              borderRadius: '12px',
              fontSize: '1.1rem',
              fontWeight: '600',
              textTransform: 'none',
              transition: 'all 0.2s ease-in-out',
              boxShadow: '0 4px 12px rgba(44, 90, 160, 0.2)',
            }}
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </Box>
        <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
          <DialogContent sx={{ fontFamily: 'inherit', color: modalStatus === 'success' ? '#1a962b' : '#d32f2f', textAlign: 'center', minWidth: 300 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, fontFamily: 'inherit' }}>
              {modalStatus === 'success' ? 'Success' : 'Error'}
            </Typography>
            <Typography sx={{ fontFamily: 'inherit' }}>{modalMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)} color="primary" sx={{ fontFamily: 'inherit' }}>OK</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Contact;

